// PriceListService.tsx
import { BaseService } from '../../core/BaseService';
import { axiosClient } from '../../core/axios';
import { ChangeStatusRequest, PaginationParams } from '../../type';
import { ImportResponse } from '../allcommon/interface';
import {
  AssignItemRequest,
  AssignPackageRequest,
  AssignServiceRequest,
  AssignUnassignResponse,
  ChangeStatusPriceListResponse,
  DeletePriceListResponse,
  GetPriceListDropdownResponse,
  GetPriceListItemResponse,
  GetPriceListPackageResponse,
  GetPriceListResponse,
  GetPriceListServiceResponse,
  GetPricelistByIdResponse,
  PriceListRequest,
  PriceListResponse,
  UnassignItemRequest,
  UnassignPackageRequest,
  UnassignServiceRequest,
} from './interface';

export class PriceListProfile extends BaseService {
  async createPriceList(req: PriceListRequest) {
    return this.post<PriceListResponse, PriceListRequest>(`${this.apiUrl}/api/pricelists`, req);
  }

  async getPriceList(params: PaginationParams) {
    return this.get<GetPriceListResponse>(`${this.apiUrl}/api/pricelists/paged`, params);
  }

  async getPriceListDropdown(params: PaginationParams) {
    return this.get<GetPriceListDropdownResponse>(`${this.apiUrl}/api/pricelists/get`, params);
  }

  async updatePriceList(id: number, req: PriceListRequest) {
    return this.put<PriceListResponse, PriceListRequest>(`${this.apiUrl}/api/pricelists/${id}`, req);
  }

  async deletePriceList(id: number) {
    return this.delete<DeletePriceListResponse>(`${this.apiUrl}/api/pricelists/${id}`);
  }

  async changeStatusPriceList(id: number, active: string, req: ChangeStatusRequest) {
    return this.put<ChangeStatusPriceListResponse, ChangeStatusRequest>(
      `${this.apiUrl}/api/pricelists/${active}/${id}`,
      req
    );
  }

  async getPriceListPackage(params: PaginationParams & { pricelistId: number }) {
    return this.get<GetPriceListPackageResponse>(`${this.apiUrl}/api/packages/bypricelistid`, params);
  }

  async assignPackage(req: AssignPackageRequest) {
    return this.post<AssignUnassignResponse, AssignPackageRequest>(`${this.apiUrl}/api/packages/pricelist/assign`, req);
  }

  async unassignPackage(req: UnassignPackageRequest) {
    return this.put<AssignUnassignResponse, UnassignPackageRequest>(
      `${this.apiUrl}/api/packages/pricelist/unassign`,
      req
    );
  }

  async getPriceListService(params: PaginationParams & { pricelistId: number }) {
    return this.get<GetPriceListServiceResponse>(`${this.apiUrl}/api/services/bypricelistid`, params);
  }

  async assignService(req: AssignServiceRequest) {
    return this.post<AssignUnassignResponse, AssignServiceRequest>(`${this.apiUrl}/api/services/pricelist/assign`, req);
  }

  async unassignService(req: UnassignServiceRequest) {
    return this.put<AssignUnassignResponse, UnassignServiceRequest>(
      `${this.apiUrl}/api/services/pricelist/unassign`,
      req
    );
  }

  async getPriceListItem(params: PaginationParams & { pricelistId: number }) {
    return this.get<GetPriceListItemResponse>(`${this.apiUrl}/api/items/bypricelistid`, params);
  }

  async assignItem(req: AssignItemRequest) {
    return this.post<AssignUnassignResponse, AssignItemRequest>(`${this.apiUrl}/api/items/pricelist/assign`, req);
  }

  async unassignItem(req: UnassignItemRequest) {
    return this.put<AssignUnassignResponse, UnassignItemRequest>(`${this.apiUrl}/api/items/pricelist/unassign`, req);
  }

  async updatePriceListItem(req: AssignItemRequest) {
    return this.put<AssignUnassignResponse, AssignItemRequest>(`${this.apiUrl}/api/items/pricelist/update`, req);
  }

  async updatePriceListService(req: AssignServiceRequest) {
    return this.put<AssignUnassignResponse, AssignServiceRequest>(`${this.apiUrl}/api/services/pricelist/update`, req);
  }

  async updatePriceListPackage(req: AssignPackageRequest) {
    return this.put<AssignUnassignResponse, AssignPackageRequest>(`${this.apiUrl}/api/packages/pricelist/update`, req);
  }

  async getPricelistById(id: number) {
    return this.get<GetPricelistByIdResponse>(`${this.apiUrl}/api/pricelists/${id}`);
  }

  // Uses axiosClient directly to bypass BaseService cancel-token logic which
  // would abort the long-running download if React Query fires any concurrent GET.
  async downloadPriceList(
    type: 'pkg' | 'svc' | 'item',
    params: { page: number; size: number; pricelistId: number; title?: string }
  ) {
    return axiosClient
      .get<Blob>(`${this.apiUrl}/api/pricelists/${type}/sample/download`, {
        params,
        responseType: 'blob',
      })
      .then((res) => res.data);
  }

  /**
   * Segment for Excel import URLs — mirrors CommonProfile:
   * POST /api/{segment}/upload (parse preview) and POST /api/{segment}/create/excel (commit).
   */
  private priceListExcelSegment(type: 'package' | 'service' | 'item'): string {
    const m = {
      package: 'upload-package-prices',
      service: 'upload-service-prices',
      item: 'upload-item-prices',
    } as const;

    return `pricelists/${m[type]}`;
  }

  /**
   * Step 1 — parse/preview. Uses the same route as one-shot upload; many backends only expose
   * POST /api/pricelists/upload-{type}-prices (no extra /upload segment). Returns parsed rows in `data`.
   */
  async uploadPriceListFilePreview(type: 'package' | 'service' | 'item', pricelistId: number, file: File) {
    return this.uploadPriceList(type, pricelistId, file);
  }

  /** Step 2 — same pattern as CommonProfile.importEntity (/api/{type}/create/excel). Server creates missing rows. */
  async importPriceListExcel(
    type: 'package' | 'service' | 'item',
    pricelistId: number,
    rows: unknown
  ): Promise<ImportResponse> {
    return axiosClient
      .post<ImportResponse>(`${this.apiUrl}/api/${this.priceListExcelSegment(type)}/create/excel`, rows, {
        params: { pricelistId },
      })
      .then((res) => res.data);
  }

  /** Legacy one-shot upload (kept for callers that expect a single request). */
  async uploadPriceList(type: 'package' | 'service' | 'item', pricelistId: number, file: File) {
    const formData = new FormData();

    formData.append('file', file);

    return axiosClient
      .post(`${this.apiUrl}/api/pricelists/upload-${type}-prices`, formData, {
        params: { pricelistId },
      })
      .then((res) => res.data);
  }
}
